import React from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'hooks';
import useLabelTranslation from 'hooks/useLabelTranslation';

import {
  useLoginGateMFAMutation, useGetGatesContextQuery,
} from 'modules/gates/service';
import { cancelVerify } from 'modules/gates/reducer';
import { getGateMFAData, getSelectedDealerId, getGateConfig } from 'modules/gates/selectors';
import { getSearch } from 'modules/router/selectors';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import { addNotification } from 'modules/notifications/actions';
import { NotificationType } from 'modules/notifications/types/Notification';

import VerifyCodeForm from 'components/Login/VerifyCodeForm';
import ResendGateCodeModal from './ResendGateCodeModal';
import GateAccessModal from './GateAccessModal';
import { GateType } from 'modules/gates/types/Gate';

const Verify: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getLabelTranslation } = useLabelTranslation();

  const [code, setCode] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const search = useSelector(getSearch);
  const config = useSelector(getGateConfig);
  const mfaData = useSelector(getGateMFAData);
  const selectedDealerId = useSelector(getSelectedDealerId);

  const { data: context } = useGetGatesContextQuery();
  const [login, { isLoading }] = useLoginGateMFAMutation();

  const hasConfig = Boolean(config);
  const isRelay = config?.type === GateType.RELAY;
  const shouldShowGateModal = hasConfig && !isRelay;

  const confidentialityPolicyLink = React.useMemo(() => {
    const dealer = context?.dealers?.find((d) => d.id === selectedDealerId);
    return dealer?.confidentialityPolicyLink
      ? getLabelTranslation(dealer.confidentialityPolicyLink)
      : undefined;
  }, [context?.dealers, getLabelTranslation, selectedDealerId]);

  const handleFinish = React.useCallback(() => {
    navigate(`success${search}`);
  }, [navigate, search]);

  const handleVerify = React.useCallback(async () => {
    if (mfaData) {
      try {
        await login({
          code,
          gateId: mfaData.gateId,
          connectionId: mfaData.connectionId,
          phoneNumber: mfaData.phoneNumber,
        }).unwrap();
      } catch {
        dispatch(addNotification({
          title: { id: 'login.error.title', defaultMessage: 'Unable to login' },
          description: {
            id: 'gates.error.verificationFailed',
            defaultMessage: 'Verification failed. Please check your code and try again.',
          },
          type: NotificationType.ERROR,
        }));
      }
    }
  }, [login, code, mfaData, dispatch]);

  const toggleModal = React.useCallback(() => setIsModalOpen((prev) => !prev), []);

  const handleBack = React.useCallback(() => {
    dispatch(cancelVerify());
  }, [dispatch]);

  return (
    <>
      <VerifyCodeForm
        code={code}
        onCodeChange={setCode}
        onVerify={handleVerify}
        onPrev={handleBack}
        isLoading={isLoading}
        isDisabled={code.length < 6}
        channel={ChannelEnum.SMS}
        contactInfo={mfaData?.phoneNumber}
        onResend={toggleModal}
        confidentialityPolicyLink={confidentialityPolicyLink}
      />
      {isModalOpen && <ResendGateCodeModal onClose={setIsModalOpen} />}
      {shouldShowGateModal && (
        <GateAccessModal
          config={config}
          onFinish={handleFinish}
        />
      )}
    </>
  );
};

export default Verify;
